import React, { useState } from "react";
import { ClipboardCheck, MessageSquareText, Play, Check, PackageCheck, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";

const isWork = (s) => s.name && !s.discount && Number(s.unit_price) >= 0 && !/korting|discount|kleinmateriaal|milieubijdrage/i.test(s.name);
const fmtMin = (m) => (m ? `${Math.floor(m / 60)}u${String(Math.round(m % 60)).padStart(2, "0")}` : "");

/* Step 2: the mechanic's work list — tick jobs done / parts fitted, add remarks. Mirrors the printed work card. */
export const MechanicWorklist = ({ card, running, busy, locked, t, onTick, onStart }) => {
  const [editing, setEditing] = useState(null); // {kind,key,value}
  const services = (card.services || []).filter(isWork);
  const parts = [...(card.parts_used || []).map((p) => ({ ...p, _kind: "part", _key: p.txn_id || `${p.item_id}-${p.name}` })), ...(card.special_parts || []).map((p) => ({ ...p, _kind: "special", _key: p.id }))];
  const doneS = services.filter((s) => s.done).length, doneP = parts.filter((p) => p.fitted).length;
  const total = services.length + parts.length, done = doneS + doneP;

  const Remark = ({ kind, k, value }) => {
    const open = editing && editing.kind === kind && editing.key === k;
    if (open) {
      return (
        <div className="flex gap-2 mt-2">
          <Input autoFocus value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} placeholder={t("wl_remark_ph")} className="h-10 text-sm" data-testid={`wl-remark-input-${k}`}
            onKeyDown={(e) => { if (e.key === "Enter") { onTick(kind, k, { remark: editing.value }); setEditing(null); } if (e.key === "Escape") setEditing(null); }} />
          <button type="button" onClick={() => { onTick(kind, k, { remark: editing.value }); setEditing(null); }} className="h-10 px-3 rounded-full bg-primary text-primary-foreground text-sm font-medium" data-testid={`wl-remark-save-${k}`}>{t("save")}</button>
        </div>
      );
    }
    return (
      <button type="button" disabled={locked} onClick={() => setEditing({ kind, key: k, value: value || "" })} className={`mt-1.5 inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 border ${value ? "border-amber-500/40 bg-amber-500/10 text-amber-800" : "border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary"}`} data-testid={`wl-remark-${k}`}>
        <MessageSquareText className="h-3.5 w-3.5" />{value || t("wl_add_remark")}
      </button>
    );
  };

  const Tick = ({ on, onClick, testid, label }) => (
    <button type="button" disabled={busy || locked} onClick={onClick} aria-label={label} data-testid={testid}
      className={`h-12 w-12 shrink-0 rounded-xl border-2 grid place-items-center transition-colors ${on ? "bg-emerald-500 border-emerald-500 text-white" : "border-border bg-background hover:border-emerald-500"}`}>
      {on && <Check className="h-6 w-6" strokeWidth={3} />}
    </button>
  );

  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="mechanic-step-worklist">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-base md:text-lg flex items-center gap-2"><span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-mono font-bold text-sm">2</span><ClipboardCheck className="h-4 w-4 text-primary" />{t("wl_title")}</h2>
        <div className="text-sm font-mono font-bold" data-testid="wl-progress"><span className={done === total && total ? "text-emerald-600" : ""}>{done}/{total}</span></div>
      </div>
      {total > 0 && <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>}
      {total === 0 && <p className="text-xs text-muted-foreground" data-testid="wl-empty">{t("wl_empty")}</p>}

      {services.length > 0 && (
        <div className="space-y-2" data-testid="wl-services">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{t("wl_jobs")} · {doneS}/{services.length}</div>
          {services.map((s) => {
            const k = s.id || s.name;
            const active = running && Array.from(new Set((running.note || "").split(" · ").map((x) => x.trim()).filter(Boolean))).join(" · ") === s.name;
            return (
              <div key={k} className={`rounded-xl border p-3 flex items-start gap-3 ${s.done ? "border-emerald-500/40 bg-emerald-500/5" : active ? "border-emerald-500 bg-emerald-500/10" : "border-border"}`} data-testid={`wl-service-${k}`}>
                <Tick on={!!s.done} onClick={() => onTick("service", k, { done: !s.done })} testid={`wl-service-done-${k}`} label={t("wl_done")} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold leading-snug ${s.done ? "line-through opacity-70" : ""}`}>{s.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                    {s.std_minutes ? `${t("wl_target")} ${fmtMin(Number(s.std_minutes) * Number(s.quantity || 1))}` : ""}
                    {s.done && s.done_at ? ` · ✓ ${new Date(s.done_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ${s.done_by || ""}` : ""}
                  </div>
                  <Remark kind="service" k={k} value={s.remark} />
                </div>
                {!s.done && !active && !locked && (
                  <button type="button" disabled={busy} onClick={() => onStart(s.name)} className="h-12 w-12 shrink-0 rounded-xl bg-emerald-500 text-white grid place-items-center hover:bg-emerald-600" aria-label={t("clockIn")} data-testid={`wl-service-start-${k}`}><Play className="h-5 w-5" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {parts.length > 0 && (
        <div className="space-y-2" data-testid="wl-parts">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{t("wl_parts")} · {doneP}/{parts.length}</div>
          {parts.map((p) => {
            const st = (p.status || "").toLowerCase();
            const special = p._kind === "special";
            const waiting = special && st && !["received", "arrived", "in_stock", "fitted"].includes(st);
            return (
              <div key={p._key} className={`rounded-xl border p-3 flex items-start gap-3 ${p.fitted ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"} ${p.returned ? "opacity-50" : ""}`} data-testid={`wl-part-${p._key}`}>
                <Tick on={!!p.fitted} onClick={() => onTick(p._kind, p._key, { done: !p.fitted })} testid={`wl-part-fitted-${p._key}`} label={t("wl_fitted")} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold leading-snug ${p.fitted ? "line-through opacity-70" : ""} ${p.returned ? "line-through" : ""}`}><span className="font-mono text-primary mr-1.5">{Number(p.quantity)}×</span>{p.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5 flex flex-wrap gap-x-2">
                    {(p.sku || p.part_number) && <span>{p.sku || p.part_number}</span>}
                    {special ? (
                      <span className={`inline-flex items-center gap-1 ${waiting ? "text-orange-600" : "text-emerald-700"}`}>{waiting ? <Truck className="h-3 w-3" /> : <PackageCheck className="h-3 w-3" />}{waiting ? `${t("wl_ordered")}${p.expected_date ? ` · ${String(p.expected_date).slice(0, 10)}` : ""}` : t("wl_arrived")}</span>
                    ) : (p.location && <span>{p.location}</span>)}
                    {p.fitted && p.fitted_at && <span className="text-emerald-700">✓ {new Date(p.fitted_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {p.fitted_by || ""}</span>}
                  </div>
                  <Remark kind={p._kind} k={p._key} value={p.remark} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
